import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, Button, CardView, HelpCallToAction, Indicator, Separator, Spacer } from 'design-system';
import Amount from 'design-system/Amount';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconBoldCalendar, IconMoneyBill } from 'design-system/icons';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconPieChart from 'design-system/icons/IconPieChart';
import { IconArrowBack } from 'design-system/icons/IconArrowBack';
import IconTooltip from 'design-system/icons/IconTooltip';
import Logo from 'design-system/Logo';
import Modal from 'utils/packages/Modal';
import SectionHeader from 'design-system/SectionHeader';
import { TextButtonLinkSeeAll } from 'design-system/TextButton';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import Squircle from 'design-system/Squircle';
import Status from 'design-system/Status';
import Text from 'design-system/Text';
import ToolTip from 'design-system/ToolTip';
import { hitSlopS, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatSortCodeAndAccountNumber } from 'utils/formatting';
import AutosavePauseModal from 'features/pots/components/AutosavePauseModal';
import { formatNextCollectionDate } from 'features/pots/utils';
import AutoinvestActivityRowItem from '../components/AutoinvestActivityItem';
import { useConnectedAccount } from '../hooks/selectors';
import { getAutoInvest, getAutoInvestActivity, resumeAutoInvestPlan } from '../actions/autoinvest';
import { selectActiveTradingAccountId, selectAutoinvestMinAmount, selectAutoInvestPlanForAccount, selectAutoInvestStatusObj, selectLatestAutoinvestActivity, selectTradingAccount, } from '../reducers/selectors';
import { formatPausedUntil, formatUpcomingDate, getAutoInvestActionRequiredLabel } from '../utils/autoinvest';
import { AccountTypeDisplayLong, fundingAccountToolTipStrings } from '../constants';
import { isWeb } from '../../../constants';
import { blockInvestActionOnWeb } from '../webGate';
const emptyAllocations = [];
const AutoinvestScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const connectedAccount = useConnectedAccount();
    const tradingAccount = useAppSelector(selectTradingAccount);
    const accountId = useAppSelector(selectActiveTradingAccountId);
    const plan = useAppSelector((state) => (accountId ? selectAutoInvestPlanForAccount(state, accountId) : undefined));
    const latestActivity = useAppSelector(selectLatestAutoinvestActivity);
    const [, , loadingAction, fetchAction] = useFetchOld(muteErrorAndResult);
    useEffect(() => {
        if (accountId) {
            dispatch(getAutoInvest(accountId));
            dispatch(getAutoInvestActivity({ accountId, paging: { page: 1, perPage: 30 } }));
        }
    }, [accountId]);
    const statusObj = useAppSelector((state) => selectAutoInvestStatusObj(state, accountId));
    const displayStatus = statusObj?.displayStatus;
    const isActive = statusObj?.isActive ?? false;
    const isPaused = statusObj?.isPaused ?? false;
    const isPendingStatus = statusObj?.isPending ?? false;
    const isPending = latestActivity?.status === 'pending';
    const isActionRequired = statusObj?.isActionRequired ?? false;
    const autoInvestFundingAccount = useAppSelector((state) => state.invest.autoInvestFundingAccount);
    const minAutoinvestAmount = useAppSelector(selectAutoinvestMinAmount);
    // eslint-disable-next-line no-nested-ternary
    const inActiveColor = isActionRequired ? undefined : isPaused ? colors.status.offline : colors.status.informational;
    const allocations = plan?.allocations ?? emptyAllocations;
    const allocationText = useMemo(() => (allocations.length === 1 ? `1 fund` : `${allocations.length} funds`), [allocations.length]);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: 'Autoinvest',
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const fundingAccountTooltipSubtitle = useMemo(() => isPendingStatus
        ? [
            fundingAccountToolTipStrings.fundingAccountDesc,
            'We\u2019ve already sent your Direct Debit request to your bank. Until it\u2019s approved, the funding account can\u2019t be updated.',
        ]
        : fundingAccountToolTipStrings.fundingAccountDesc, [isPendingStatus]);
    const showFundingAccountTooltip = useCallback(() => {
        Modal.showConfirmation({
            title: 'Funding account',
            subtitle: fundingAccountTooltipSubtitle,
            confirmButtonTitle: 'Okay',
        });
    }, [fundingAccountTooltipSubtitle]);
    const isaRemainingAllowance = tradingAccount?.account.accountType === 'ISA'
        ? tradingAccount?.account.isaRemainingAllowanceAfterPending
        : undefined;
    const navigateToEditAmount = useCallback(() => {
        navigation.navigate('AutoinvestChooseAmount', {
            accountType: route.params.accountType,
            planId: plan?.id,
            collectionDate: plan?.dayOfMonth ?? 1,
            initialAmount: plan?.amount,
        });
    }, [route.params.accountType, plan?.id, plan?.dayOfMonth, plan?.amount]);
    const handlePauseResume = useCallback(() => {
        if (!accountId || !plan)
            return;
        if (blockInvestActionOnWeb('Autoinvest'))
            return;
        // TODO: Update pauseReason values once confirmed by backend
        if (isPaused && plan?.pauseReason === 'IsaLimitReached') {
            Modal.showConfirmation({
                title: 'You\u2019ve reached your annual allowance',
                subtitle: 'You\u2019ve already deposited \u00A320,000 this tax year. Autoinvest will remain paused until the new tax year begins.',
                confirmButtonTitle: 'Okay',
            });
            return;
        }
        if (isPaused && plan?.pauseReason === 'IsaAmountExceedsRemainder') {
            if (isaRemainingAllowance && isaRemainingAllowance.value >= minAutoinvestAmount) {
                Modal.showConfirmation({
                    title: 'Your next Autoinvest would exceed your ISA allowance',
                    subtitle: 'You\u2019ve used your full ISA allowance for this tax year. Reduce your Autoinvest amount to continue investing.',
                    confirmButtonTitle: 'Okay',
                    cancelButtonTitle: 'Change the amount',
                    onPressCancel: navigateToEditAmount,
                });
            }
            else {
                Modal.showConfirmation({
                    title: 'ISA allowance too low',
                    subtitle: `Your remaining ISA allowance is below the ${formatAmount(minAutoinvestAmount, 'GBP', 0)} minimum. Autoinvest will remain paused until the new tax year begins.`,
                    confirmButtonTitle: 'Okay',
                });
            }
            return;
        }
        if (isPaused) {
            fetchAction(resumeAutoInvestPlan(plan.id));
        }
        else {
            const isProcessing = latestActivity?.status === 'pending';
            const isUpcomingWithin24h = latestActivity?.status === 'upcoming';
            if (isProcessing) {
                const pendingAmount = formatAmount(latestActivity.amount.value, latestActivity.amount.currency, 2);
                Modal.showConfirmation({
                    title: 'Pause Autoinvest?',
                    subtitle: [
                        `Your ${pendingAmount} Autoinvest is already being processed and can\u2019t be skipped.`,
                        `Pausing now will stop future Autoinvests only.`,
                    ],
                    confirmButtonTitle: 'Pause future payments',
                    onConfirm: () => {
                        navigation.navigate('PauseAutoinvestInfoScreen', { planId: plan.id });
                    },
                    cancelButtonTitle: 'Cancel',
                });
            }
            else if (isUpcomingWithin24h) {
                const pendingAmount = formatAmount(latestActivity.amount.value, latestActivity.amount.currency, 2);
                const upcomingDate = formatUpcomingDate(latestActivity.timestamp);
                AutosavePauseModal.show({
                    title: 'Pause Autoinvest?',
                    subtitle: `Pausing will stop future Autoinvests.\nYou still have a ${pendingAmount} autoinvest scheduled for ${upcomingDate}.\nWould you like to skip ${upcomingDate}\u2019s payment as well?`,
                    onPause: () => {
                        navigation.navigate('PauseAutoinvestInfoScreen', {
                            planId: plan.id,
                        });
                    },
                    onPauseAndSkip: () => {
                        navigation.navigate('PauseAutoinvestInfoScreen', {
                            planId: plan.id,
                            skipUpcoming: true,
                            expectedPaymentDate: latestActivity.paymentCollectionEta,
                            activityId: latestActivity.id,
                        });
                    },
                });
            }
            else {
                navigation.navigate('PauseAutoinvestInfoScreen', { planId: plan.id });
            }
        }
    }, [
        accountId,
        plan,
        isPaused,
        isaRemainingAllowance,
        minAutoinvestAmount,
        navigateToEditAmount,
        fetchAction,
        latestActivity,
    ]);
    const handleFix = useCallback(() => {
        if (blockInvestActionOnWeb('Autoinvest'))
            return;
        navigation.navigate('AutoinvestSelectFundingAccount', {
            accountType: route.params.accountType,
            collectionDate: plan?.dayOfMonth ?? 1,
            amount: plan?.amount ?? 0,
            allocations: plan?.allocations ?? [],
            planId: plan?.id,
        });
    }, [route.params.accountType, plan?.id, plan?.dayOfMonth, plan?.amount, plan?.allocations]);
    // Autoinvest is read-only on web, so every edit below shows the QR code modal
    const handleEditAmount = useCallback(() => {
        if (blockInvestActionOnWeb('Autoinvest'))
            return;
        if (isPending) {
            Modal.showConfirmation({
                title: 'Autoinvest is in progress',
                subtitle: 'We’re currently processing your scheduled investment for today.\nAny changes you make will apply to future collections.',
                confirmButtonTitle: 'Okay',
                onConfirm: navigateToEditAmount,
            });
        }
        else {
            navigateToEditAmount();
        }
    }, [isPending, navigateToEditAmount]);
    const navigateToEditAllocation = useCallback(() => {
        const initialAllocations = allocations.map((a) => ({
            assetId: a.assetId,
            percentage: a.percentage * 100,
        }));
        navigation.navigate('AutoinvestAllocation', {
            accountType: route.params.accountType,
            planId: plan?.id,
            isEditMode: true,
            initialAllocations,
        });
    }, [route.params.accountType, plan?.id, allocations]);
    const handleEditAllocation = useCallback(() => {
        if (blockInvestActionOnWeb('Autoinvest'))
            return;
        navigateToEditAllocation();
    }, [navigateToEditAllocation]);
    const navigateToEditCollectionDate = useCallback(() => {
        navigation.navigate('AutoinvestPickCollectionDate', {
            accountType: route.params.accountType,
            planId: plan?.id,
            initialCollectionDate: plan?.dayOfMonth,
        });
    }, [route.params.accountType, plan?.id, plan?.dayOfMonth]);
    const handleSeeAll = useCallback(() => {
        navigation.navigate('AutoinvestActivities', { accountType: route.params.accountType });
    }, [route.params.accountType]);
    const handleEditCollectionDate = useCallback(() => {
        if (blockInvestActionOnWeb('Autoinvest'))
            return;
        if (isPending) {
            Modal.showConfirmation({
                title: 'Autoinvest is in progress',
                subtitle: 'Weʼre currently processing your scheduled investment for today.\nAny changes you make will apply to future collections.',
                confirmButtonTitle: 'Okay',
                onConfirm: navigateToEditCollectionDate,
            });
        }
        else {
            navigateToEditCollectionDate();
        }
    }, [isPending, navigateToEditCollectionDate]);
    const onHelpPress = useCallback(() => {
        navigation.navigate('Help', {}, { pop: true });
    }, []);
    if (!plan) {
        return (<AppView type="secondary" withFrame={false}>
          <Indicator dark/>
        </AppView>);
    }
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          {/* Section 1: Top card */}
          <CardView noMarginBottom style={styles.topCard}>
            <View style={styles.rowAccount}>
              <Squircle color={colors.background.dark}>
                <IconRocket color={colors.text.investment}/>
              </Squircle>
              <Spacer.W16 />
              <View style={styles.flex}>
                <Status active={isActive} activeText="Working" inactiveText={isActionRequired ? 'Action required' : displayStatus ?? ''} inActiveColor={inActiveColor}/>
                <Text Text-16>Autoinvest</Text>
              </View>
            </View>

            <Separator />
            <Spacer.H16 />

            {isPaused && plan?.pausedUntil ? (<View style={styles.textWrapper}>
                <Text TextThin-14 Secondary>
                  {formatPausedUntil(plan.pausedUntil)}
                </Text>
              </View>) : null}

            {isPendingStatus && !isActionRequired ? (<View style={styles.pendingRow}>
                <Text TextThin-14 Secondary>
                  It might take up to 5 working days for the Bank to approve Direct Debit.
                </Text>
              </View>) : null}

            {statusObj?.isActionRequired ? (<View style={styles.textWrapper}>
                <Text TextThin-14 Secondary>
                  {getAutoInvestActionRequiredLabel(statusObj.isActionRequired, autoInvestFundingAccount?.bankName, tradingAccount ? AccountTypeDisplayLong[tradingAccount.account.accountType] : undefined)}
                </Text>
              </View>) : (<>
                <View style={styles.fundingAccountTitleRow}>
                  <Text TextThin-14 Secondary>
                    Funding account
                  </Text>
                  <Spacer w={6}/>
                  {isWeb ? (
            // On web the explanation shows on hover rather than in a modal
            <ToolTip small colorKey="secondary" title="Funding account" subtitle={fundingAccountTooltipSubtitle}/>) : (<TouchableOpacity hitSlop={hitSlopS} onPress={showFundingAccountTooltip}>
                      <IconTooltip height={16} width={16} colorKey="secondary"/>
                    </TouchableOpacity>)}
                </View>

                {connectedAccount ? (<View style={styles.bankInfoCard}>
                    <Logo R24 onColoredBackground uri={connectedAccount.bankIconUrl}/>
                    <Spacer w={16}/>
                    <View style={styles.flex}>
                      <Text TextThin-14 Secondary numberOfLines={1} ellipsizeMode="tail">
                        {formatSortCodeAndAccountNumber(connectedAccount.ukSortCode, connectedAccount.ukAccountNumber)}
                      </Text>
                    </View>
                  </View>) : null}
              </>)}

            <View style={styles.pauseButtonContainer}>
              {isActionRequired ? (<Button square errorReversed onPress={handleFix} title="Let's fix this"/>) : (<Button square brandReversed loading={loadingAction} onPress={handlePauseResume} title={isPaused ? 'Resume Autoinvest' : 'Pause Autoinvest'}/>)}
            </View>
            <Spacer h={16}/>
          </CardView>

          {/* Section 2: Activity */}
          <Spacer h={24}/>
          <SectionHeader horizontalMargins button={latestActivity ? <TextButtonLinkSeeAll onPress={handleSeeAll}/> : null}>
            Activity
          </SectionHeader>
          <Spacer.H16 />
          {latestActivity ? (<AutoinvestActivityRowItem activity={latestActivity}/>) : (<CardView noMarginTop noMarginBottom withPadding>
              <Text TextThin-14 Secondary>
                Here you will see past investments and the upcoming investment 24 hours before it{'\u2019'}s due.
              </Text>
            </CardView>)}

          {/* Section 3: Collection details */}
          <Spacer h={24}/>
          <SectionHeader horizontalMargins>Collection details</SectionHeader>
          <Spacer.H16 />
          <CardView noMarginTop noMarginBottom webPadding>
            <SettingsItemNavigate description="Amount" title={<Amount Numbers-16 value={{ value: plan.amount, currency: 'GBP' }}/>} icon={<IconMoneyBill gradientKey="darkPink"/>} onPress={handleEditAmount}/>
            <SettingsItemNavigate description="Allocation" title={allocationText} icon={<IconPieChart gradientKey="yellow"/>} onPress={handleEditAllocation}/>
            <SettingsItemNavigate disabled title="Monthly" description="Collection frequency" icon={<IconArrowBack gradientKey="apricot"/>}/>
            {isActive ? (<SettingsItemNavigate description="Next collection" title={formatNextCollectionDate(plan.nextPaymentDate)} icon={<IconBoldCalendar gradientKey="skyBlue"/>} onPress={handleEditCollectionDate}/>) : null}
          </CardView>
          <Spacer.H16 />
          <HelpCallToAction onDark onPress={onHelpPress} title="Want to have more details?" subTitle="Learn more about Autoinvest"/>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: 'Autoinvest',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    topCard: {
        marginTop: rem(16),
    },
    flex: {
        flex: 1,
    },
    rowAccount: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    fundingAccountTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    pauseButtonContainer: {
        paddingHorizontal: rem(16),
        marginTop: rem(8),
    },
    pendingRow: {
        paddingTop: 0,
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    textWrapper: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
    bankInfoCard: {
        paddingVertical: rem(12),
        paddingHorizontal: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
        borderRadius: rem(12),
        marginBottom: rem(16),
        marginHorizontal: rem(16),
        backgroundColor: colors.background.dark,
    },
}));
export default AutoinvestScreen;
